import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiJson } from './apiClient';
import { branchQuerySuffix, readOverviewBranchFilter } from './components/OverviewCommandPanel';
import { normalizeRole } from './utils/userLocale';
import { roundMoney2 } from './utils/money';

export const SalesHistoryContext = createContext();

function normalizeSale(s) {
  if (!s) return null;
  return {
    id: s.id,
    branchId: s.branchId,
    date: s.date,
    product: s.product
      ? {
          id: s.product.id,
          name: s.product.name,
          category: s.product.category,
        }
      : null,
    quantity: s.quantity,
    paymentMethod: s.paymentMethod,
    paymentLabel: s.paymentLabel,
    amount: s.amount != null ? roundMoney2(s.amount) : undefined,
    cost: s.cost != null ? roundMoney2(s.cost) : undefined,
    profit: s.profit != null ? roundMoney2(s.profit) : undefined,
  };
}

export const SalesHistoryProvider = ({ children }) => {
  const { user } = useAuth();
  const [salesHistory, setSalesHistory] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);

  const refreshSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const branchId = normalizeRole(user?.role) === 'owner' ? readOverviewBranchFilter() : '';
      const data = await apiJson(`/sales${branchQuerySuffix(branchId)}`);
      const list = Array.isArray(data.sales) ? data.sales.map(normalizeSale).filter(Boolean) : [];
      setSalesHistory(list);
    } catch {
      setSalesHistory([]);
    } finally {
      setSalesLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSalesHistory([]);
      setSalesLoading(false);
      return undefined;
    }
    void refreshSales();
    const onBranch = () => void refreshSales();
    window.addEventListener('cbonline:branch-filter', onBranch);
    return () => window.removeEventListener('cbonline:branch-filter', onBranch);
  }, [user, refreshSales]);

  /**
   * Records a sale on the server and prepends the returned row to local history.
   * @returns {Promise<object>} normalized sale
   */
  const recordSale = async ({
    productId,
    quantity,
    paymentMethod,
    paymentLabel,
    amountPaid,
    deliverToSite,
    customerName,
    customerPhone,
    destination,
  }) => {
    const data = await apiJson('/sales', {
      method: 'POST',
      body: JSON.stringify({
        productId,
        quantity,
        paymentMethod,
        paymentLabel: paymentLabel ?? paymentMethod,
        amountPaid: amountPaid != null ? Number(amountPaid) : null,
        deliverToSite: !!deliverToSite,
        customerName: customerName?.trim() || null,
        customerPhone: customerPhone?.trim() || null,
        destination: destination?.trim() || null,
      }),
    });
    const normalized = normalizeSale(data.sale);
    if (normalized) {
      setSalesHistory((prev) => [normalized, ...prev]);
    }
    return { sale: normalized, delivery: data.delivery ?? null, debt: data.debt ?? null };
  };

  return (
    <SalesHistoryContext.Provider value={{ salesHistory, salesLoading, recordSale, refreshSales }}>
      {children}
    </SalesHistoryContext.Provider>
  );
};
